import { useEffect, useMemo, useState } from 'react';

type Props<T> = {
    items: T[];
    itemsPerPage?: number;
    /**
     * Optional filter identity. When set, the page is only reset to the first
     * page when this key changes; a refetch of `items` keeps the current page
     * (clamped to the last available page). Without it, every change of
     * `items` resets to the first page (legacy behaviour).
     */
    resetKey?: string;
};

export type UsePaginationReturn<T> = {
    currentItems: T[];
    setNextPage: (page: number) => void;
    page: number;
    setItemsPerPage: (itemsPerPage: number) => void;
    itemsPerPage: number;
    countTotal: number;
};

/**
 * Client-side pagination for `StarjackTable`. Slices `items` into the current
 * page and resets to the first page whenever `items` changes (e.g. a new
 * search/filter result).
 */
export const usePagination = <T>({
    items = [],
    itemsPerPage,
    resetKey,
}: Props<T>): UsePaginationReturn<T> => {
    const [currentPage, setCurrentPage] = useState(0);
    const [currentItemsPerPage, setCurrentItemsPerPage] = useState(
        itemsPerPage || 10
    );

    const currentItems = useMemo(() => {
        return items?.length
            ? items.slice(
                  currentPage * currentItemsPerPage,
                  currentPage * currentItemsPerPage + currentItemsPerPage
              )
            : [];
    }, [currentPage, items, currentItemsPerPage]);

    // Reset to the first page only when the dataset changes. `currentPage` must
    // NOT be a dependency here — including it makes every page change re-run the
    // effect and snap back to page 0 (the original pagination bug).
    const hasResetKey = resetKey !== undefined;

    useEffect(() => {
        if (!hasResetKey) setCurrentPage(0);
    }, [items]);

    // Opt-in: reset only when the filter changes, not on every refetch.
    useEffect(() => {
        if (hasResetKey) setCurrentPage(0);
    }, [resetKey]);

    // Opt-in: keep the page on refetch, clamp it if the list got shorter.
    useEffect(() => {
        if (!hasResetKey) return;
        const lastPage = Math.max(
            0,
            Math.ceil(items.length / currentItemsPerPage) - 1
        );
        setCurrentPage((page) => Math.min(page, lastPage));
    }, [items, currentItemsPerPage]);

    return {
        currentItems,
        setNextPage: setCurrentPage,
        page: currentPage,
        setItemsPerPage: setCurrentItemsPerPage,
        itemsPerPage: currentItemsPerPage,
        countTotal: items.length,
    };
};
